import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import SourceName from "../../../SourceName";
import RowDialog from "../RowDialog";
import TextTruncate from "components/TextTruncate";
import ThreeDotsMenu from "components/ThreeDotsMenu";
import DefaultThreeDotsMenuItem from "components/ThreeDotsMenu/Components/DefaultThreeDotsMenuItem";
import CalculationStatusIcon from "../../../CalculationStatusIcon";
import CreateEntityFromResultElementDialog from "pages/RS-Workspace/components/Results/components/CreateEntityFromResultElementDialog";
import CreateVariantFromResultElementDialog from "pages/RS-Workspace/components/Results/components/CreateVariantFromResultElementDialog";
import { EntitiesCreatableFromResultElement, ResultElementSourceAppearance, ResultSetElementType, TableElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { workspaceCellModelsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { bioreactorsListSelector } from "store/global/global.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { createEntityFromResultElementDialogDataSelector } from "store/rs-workspace/selectors/results.selectors";
import { setCreateEntityFromResultElementDialogData } from "store/rs-workspace/actions/results.actions";
import { getRowName } from "../../utils";
import { prepareItemsOnMove } from "utils/data";
import { isNotEmpty } from "utils/validate";
import { isSimulationCompleted, isSimulationVariantCompleted, } from "store/rs-workspace/utils/completeness.utils";
import { NewEntityCreationOption } from "shared/labels";
import EditIcon from "icons/settings.svg";
import DeleteIcon from "icons/delete.svg";
import BackIcon from "icons/back.svg";
import ArrowUpIcon from "icons/icn-arrow-up.svg";
import PlusIcon from "icons/plus.svg";
import * as styles from "./styles.module.scss";
const RowName = ({ row, rows, editable, accuracy, appearance, elementId, deleteRow, moveRows, upsert, }) => {
    const dispatch = useDispatch();
    const [threeDotsMenuOpen, setThreeDotsMenuOpen] = useState(false);
    const [rowDialogOpen, setRowDialogOpen] = useState(false);
    const [newEntityType, setNewEntityType] = useState(null);
    const pageInEditMode = useSelector(isEditableSelector);
    const createEntityDialogData = useSelector(createEntityFromResultElementDialogDataSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const processDatas = useSelector(workspaceProcessDataSelector);
    const bioreactors = useSelector(bioreactorsListSelector);
    const moveRow = useCallback((direction, toEnd) => {
        moveRows(prepareItemsOnMove(rows, row, direction, toEnd));
    }, [moveRows, row, rows]);
    const nameData = useMemo(() => getRowName(row, simulations, processDatas, bioreactors, optimizations), [row, simulations, optimizations, processDatas, bioreactors]);
    const menuItems = useMemo(() => {
        return [
            {
                text: "Edit",
                icon: React.createElement(EditIcon, null),
                onClick: () => setRowDialogOpen(true),
            },
            {
                text: "Delete",
                icon: React.createElement(DeleteIcon, null),
                onClick: () => row?.id && deleteRow(row?.id),
            },
            {
                text: "Move to the top",
                icon: React.createElement(BackIcon, { className: styles["moveToIcon-top"] }),
                onClick: () => moveRow("before", true),
                disabled: row.position === 1,
            },
            {
                text: "Move above",
                icon: React.createElement(ArrowUpIcon, { className: styles["moveIcon-top"] }),
                onClick: () => moveRow("before"),
                disabled: row.position === 1,
            },
            {
                text: "Move below",
                icon: React.createElement(ArrowUpIcon, { className: styles["moveIcon-bottom"] }),
                onClick: () => moveRow("after"),
                disabled: row.position === rows.length,
            },
            {
                text: "Move to the bottom",
                icon: React.createElement(BackIcon, { className: styles["moveToIcon-bottom"] }),
                onClick: () => moveRow("after", true),
                disabled: row.position === rows.length,
            },
        ];
    }, [deleteRow, moveRow, row?.id, row.position, rows.length]);
    const canCreateEntityFromSource = useMemo(() => appearance === ResultElementSourceAppearance.Calculated &&
        ((row.sourceType === TableElementSourceType.SIMULATION && !!row.adjustmentId) || !!row.optimizationId), [appearance, row.sourceType, row.adjustmentId, row.optimizationId]);
    const viewModeMenuItems = useMemo(() => [
        ...(row.optimizationId
            ? [
                {
                    text: NewEntityCreationOption.Variant,
                    icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
                    onClick: () => {
                        setNewEntityType(EntitiesCreatableFromResultElement.Variant);
                        dispatch(setCreateEntityFromResultElementDialogData({ elementId, sourceId: row.id ?? 0 }));
                    },
                },
            ]
            : []),
        {
            text: NewEntityCreationOption.Recipe,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.Recipe);
                dispatch(setCreateEntityFromResultElementDialogData({ elementId, sourceId: row.id ?? 0 }));
            },
        },
        {
            text: NewEntityCreationOption.CellModel,
            icon: React.createElement(PlusIcon, { width: 15, height: 15 }),
            onClick: () => {
                setNewEntityType(EntitiesCreatableFromResultElement.CellModel);
                dispatch(setCreateEntityFromResultElementDialogData({ elementId, sourceId: row.id ?? 0 }));
            },
        },
    ], [dispatch, elementId, row.id, row.optimizationId]);
    const displayRowErrored = useMemo(() => appearance === ResultElementSourceAppearance.Error ||
        appearance === ResultElementSourceAppearance.Incomplete, [appearance]);
    const rowNameTooltip = useMemo(() => {
        if (appearance === ResultElementSourceAppearance.Ready) {
            return "Ready to be calculated";
        }
        let entityName = "Simulation";
        if (isNotEmpty(row.adjustmentId) && row.adjustmentId !== 0) {
            const simulation = simulations?.find(({ id }) => id === row.sourceId);
            const variant = simulation?.state?.adjustments?.find(({ id }) => id === row.adjustmentId);
            if (isSimulationCompleted(simulation, cellModels, recipes) &&
                !isSimulationVariantCompleted(simulation, cellModels, recipes, variant)) {
                entityName = "Simulation variant";
            }
        }
        if (row.optimizationId !== null) {
            entityName = "Optimization";
        }
        if (appearance === ResultElementSourceAppearance.Incomplete) {
            return `The ${entityName} is no longer valid`;
        }
    }, [appearance, row.optimizationId, row.adjustmentId, row.sourceId, simulations, cellModels, recipes]);
    const renderCreateEntityDialogs = useMemo(() => {
        if (createEntityDialogData.elementId !== elementId || createEntityDialogData.sourceId !== row.id) {
            return null;
        }
        return newEntityType === EntitiesCreatableFromResultElement.Variant ? (React.createElement(CreateVariantFromResultElementDialog, { simulationId: row.sourceId, adjustmentId: row.adjustmentId, sourceId: row.id, resultElementType: ResultSetElementType.Table, elementId: elementId })) : (React.createElement(CreateEntityFromResultElementDialog, { isOpen: true, rowId: row.id, entityType: newEntityType ?? EntitiesCreatableFromResultElement.Recipe, resultElementType: ResultSetElementType.Table, elementId: elementId, entityId: row.sourceId ?? 0, onClose: () => dispatch(setCreateEntityFromResultElementDialogData({ elementId: null, sourceId: null })) }));
    }, [
        createEntityDialogData.elementId,
        createEntityDialogData.sourceId,
        dispatch,
        elementId,
        newEntityType,
        row.adjustmentId,
        row.id,
        row.sourceId,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(SourceName, { tooltip: rowNameTooltip, className: styles.rowName },
            editable && (React.createElement(ThreeDotsMenu, { name: "Actions", classes: {
                    className: classNames(styles.threeDotsMenu, {
                        [styles["threeDotsMenu-opened"]]: threeDotsMenuOpen,
                    }),
                }, items: menuItems.map((item, index) => (React.createElement(DefaultThreeDotsMenuItem, { key: index, closeMenuOnClick: () => setThreeDotsMenuOpen(false), ...item }))), isOpen: threeDotsMenuOpen, setIsOpen: setThreeDotsMenuOpen })),
            pageInEditMode && !editable && canCreateEntityFromSource && (React.createElement(ThreeDotsMenu, { name: "Actions", classes: {
                    className: classNames(styles.threeDotsMenu, styles.top, {
                        [styles["threeDotsMenu-opened"]]: threeDotsMenuOpen,
                    }),
                }, items: viewModeMenuItems.map((item, index) => (React.createElement(DefaultThreeDotsMenuItem, { key: index, closeMenuOnClick: () => setThreeDotsMenuOpen(false), ...item }))), isOpen: threeDotsMenuOpen, setIsOpen: setThreeDotsMenuOpen })),
            React.createElement("div", { className: styles["source-container"] },
                row.sourceType !== TableElementSourceType.PROCESS_DATA && (React.createElement(CalculationStatusIcon, { accuracy: accuracy, appearance: appearance })),
                React.createElement(TextTruncate, { key: row.id, className: classNames(styles["rowName-title"], {
                        [styles.error]: displayRowErrored,
                    }), text: nameData.title })),
            !!nameData.subtitle && (React.createElement(TextTruncate, { key: `subtitle-${row.id}`, className: classNames(styles["rowName-subtitle"], {
                    [styles.error]: displayRowErrored,
                }), text: nameData.subtitle })),
            !!nameData.subSubtitle && (React.createElement(TextTruncate, { key: `subSubtitle-${row.id}`, className: classNames(styles["rowName-subSubtitle"], {
                    [styles.error]: displayRowErrored,
                }), text: nameData.subSubtitle }))),
        rowDialogOpen && (React.createElement(RowDialog, { rows: rows, row: row, onSubmit: upsert, onClose: () => setRowDialogOpen(false), classes: { target: styles.rowDialogTarget } })),
        renderCreateEntityDialogs));
};
export default RowName;
